import * as React from 'react';

import { MediaResourceTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';
import _ from 'underscore';

import { MediaPlayer } from '~shared/components/MediaPlayer';
import {
  StudentApplicationContext,
  useStudentApplicationContext,
} from '~shared/context/StudentApplicationContext';
import { useMedia } from '~shared/hooks';
import { VideoResource } from '~teacher/models/quizzes';

import ActivityImage from '../../../../shared/components/activity/ActivityImage';
import StudentRocketIcon from '../../../../shared/icons/StudentRocketIcon';
import { Point } from '../../components/Point';
import QuizNav from '../../components/QuizNav';
import { QuizTimer } from '../../header/components/QuizTimer';
import { VideoErrorScreen } from './VideoErrorScreen';

export interface NavProps {
  // This is to match legacy behavior in student app
  answered:
    | boolean
    | {
        selection_answers: any[];
        text_answers: any[];
      };
  order: number;
  selected: boolean;
  type: string;
}

interface StudentQuizViewProps {
  answers: Answer[];
  controller: {
    answerClicked: (answerId: number) => void;
    answerTextChanged: (text: string) => void;
    finishQuizClicked: () => void;
    nextClicked: () => void;
    previousClicked: () => void;
    questionNumberClicked: (questionNumber: number) => void;
    submitAnswerClicked: () => void;
  };
  currentQuestion: number;
  grading_weight: number;
  imageUrl: string;
  isResponseValid: boolean;
  isSpaceRace: boolean;
  navProps: NavProps[];
  questionMedia: VideoResource;
  question_text: string;
  quizProgress: string;
  quizTimerSeconds?: number;
  onTimerExpired?: () => void;
  showGradingWeight: boolean;
  showSubmit: boolean;
  spaceRaceClass: string;
  spaceRaceTeam: string;
  studentNav: boolean;
  student_response: any;
  totalQuestions: number;
  type: string;
  isBigScreen: boolean;
}

interface Answer {
  id: number;
  text: string;
  image: { url: string };
  question_id: number;
  order: number;
}

const StudentQuizView = (props: StudentQuizViewProps) => {
  const isBigScreen = useMedia(['(min-width: 768px)'], [true], false);
  const {
    answers,
    controller,
    currentQuestion,
    grading_weight,
    imageUrl,
    isResponseValid,
    isSpaceRace,
    navProps,
    questionMedia,
    question_text,
    quizProgress,
    quizTimerSeconds,
    onTimerExpired,
    showGradingWeight,
    showSubmit,
    spaceRaceClass,
    spaceRaceTeam,
    studentNav,
    totalQuestions,
    type,
    student_response,
  } = props;

  const {
    answerClicked,
    answerTextChanged,
    finishQuizClicked,
    nextClicked,
    previousClicked,
    questionNumberClicked,
    submitAnswerClicked,
  } = controller;

  const studentApplicationContext = useStudentApplicationContext();

  const textAreaRef = React.useRef(null);

  React.useEffect(() => {
    if (type === 'FR') {
      const response = student_response;

      let textResponseValue = '';

      if (studentApplicationContext === StudentApplicationContext.Socrative) {
        if (
          response &&
          response.has('text_answers') &&
          response.get('text_answers').length > 0
        ) {
          textResponseValue = _.unescape(
            response.get('text_answers')[0].answer_text
          );
        }
      } else if (
        studentApplicationContext ===
        StudentApplicationContext.ShowbieAssessments
      ) {
        if (response?.text_answers?.length > 0) {
          textResponseValue = _.unescape(
            response?.text_answers[0]?.answer_text ?? ''
          );
        }
      }
      textAreaRef.current.value = textResponseValue;
    }
  }, [student_response, type, studentApplicationContext]);

  const createAnswerLetter = (number: number, isTrueFalse: boolean) => {
    if (isTrueFalse) {
      return number === 1 ? 'T' : 'F';
    }

    if (number > 26) {
      const baseChar = 'A'.charCodeAt(0);
      let letters = '';

      do {
        number -= 1;
        letters = String.fromCharCode(baseChar + (number % 26)) + letters;
        number = (number / 26) >> 0;
      } while (number > 0);

      return letters;
    }

    return String.fromCharCode('A'.charCodeAt(0) + number - 1);
  };

  const createAnswerContent = (answer: Answer, isTrueFalse: boolean) => {
    const textClass = cx('mc-answer-option-text', {
      'wrap-text': answer.text.length > 50,
    });
    const hasText = answer.text && answer.text.length > 0;
    return (
      <div
        className="answerContentWrapper"
        onClick={() => answerClicked(answer.id)}
      >
        {hasText && (
          <div className={textClass}>
            <pre
              dangerouslySetInnerHTML={{
                __html: isTrueFalse ? translate(answer.text) : answer.text,
              }}
            />
          </div>
        )}

        {answer.image && (
          <ActivityImage imageUrl={answer.image.url} isAnswerImage={true} />
        )}
      </div>
    );
  };

  const createAnswerOption = (
    answer: Answer,
    number: number,
    isTrueFalse?: boolean
  ) => {
    let selectedClass = '';
    let selectionAnswers = [];
    if (studentApplicationContext === StudentApplicationContext.Socrative) {
      selectionAnswers = student_response
        ? student_response.get('selection_answers')
        : [];
    } else if (
      studentApplicationContext === StudentApplicationContext.ShowbieAssessments
    ) {
      selectionAnswers = student_response?.selection_answers ?? [];
    }

    if (selectionAnswers && selectionAnswers.length) {
      for (let i = 0; i < selectionAnswers.length; i++) {
        if (selectionAnswers[i].answer_id == answer.id) {
          selectedClass = ' selected';
          break;
        }
      }
    }

    return (
      <div
        key={`${answer.order}-${answer.question_id}`}
        className={cx('mc-answer-option', selectedClass)}
      >
        <div className="mc-answer-option-container">
          <span
            className="answer-option-letter"
            onClick={() => answerClicked(answer.id)}
          >
            {createAnswerLetter(number, isTrueFalse)}
          </span>
          {createAnswerContent(answer, isTrueFalse)}
        </div>
      </div>
    );
  };

  const answerTextFocused = () => {
    if (!isBigScreen) {
      const nav = document.getElementById('student-nav-container');

      if (nav) {
        nav.style.bottom = '-70px';
        nav.style.position = 'absolute';
      }
    }
  };

  const answerTextBlurred = () => {
    if (!isBigScreen) {
      const nav = document.getElementById('student-nav-container');

      if (nav) {
        nav.style.bottom = '0';
        nav.style.position = 'fixed';
      }
    }
  };

  const questionAnswerContainerChildren = [];

  if (type === 'FR') {
    questionAnswerContainerChildren.push(
      <textarea
        key="questionAnswerContainerChildren-textarea-1"
        className="fr-question-textarea"
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        maxLength={65000}
        placeholder={translate('Enter Answer Here')}
        ref={textAreaRef}
        spellCheck={false}
        onFocus={answerTextFocused}
        onBlur={answerTextBlurred}
        onChange={(event) => answerTextChanged(event.target.value)}
      />
    );
  } else if (type === 'MC') {
    const mcAnswerAreaChildren = [];

    for (let j = 0; j < answers.length; j++) {
      mcAnswerAreaChildren.push(createAnswerOption(answers[j], j + 1));
    }

    questionAnswerContainerChildren.push(
      <div
        className="mc-answer-area"
        key="questionAnswerContainerChildren-div-1"
      >
        {mcAnswerAreaChildren}
      </div>
    );
  } else if (type === 'TF') {
    let trueOption = answers[0];
    let falseOption = answers[1];

    // Can't always rely on orders, use text instead
    const options = _.partition(answers, (answer) => {
      return answer.text === 'True';
    });

    // This should always happen
    if (options.length === 2) {
      trueOption = options[0][0];
      falseOption = options[1][0];
    }

    questionAnswerContainerChildren.push(
      <div
        className="mc-answer-area"
        key="questionAnswerContainerChildren-div-2"
      >
        {createAnswerOption(trueOption, 1, true)}
        {createAnswerOption(falseOption, 2, true)}
      </div>
    );
  }

  const navContainerChildren = [];
  const navPaginationChildren = [];

  if (showSubmit) {
    navContainerChildren.push(
      <button
        key="navContainerChildren-button-1"
        id="submit-button"
        className="button button-primary button-large"
        disabled={!isResponseValid && type !== 'FR'}
        onClick={submitAnswerClicked}
      >
        {translate('Submit Answer')}
      </button>
    );
  } else {
    navPaginationChildren.push(
      <QuizNav
        key="navContainerChildren-QuizNav-1"
        currentQuestion={currentQuestion}
        nextClicked={nextClicked}
        questionClicked={questionNumberClicked}
        navProps={navProps}
        previousClicked={previousClicked}
        totalQuestions={totalQuestions}
      />
    );
  }

  const progressChildren = [];

  if (isSpaceRace) {
    const teamName = translate('Team') + ' ' + translate(spaceRaceTeam);
    progressChildren.push(
      <div
        className={cx('space-race-progress-container clearfix', spaceRaceClass)}
        key="space-race-progress-container"
      >
        <div className="space-race-progress">
          <span className="space-race-progress-text">{quizProgress}</span>
          <div className="space-race-team">
            <StudentRocketIcon className="student-rocket-icon" />
            <span className="space-race-team-text">{teamName}</span>
          </div>
        </div>
      </div>
    );
  } else {
    const quizProgressLeftChildren = [
      <span key="quiz-progress-text" className="quiz-progress-text">
        {quizProgress}
      </span>,
    ];

    if (studentNav) {
      let questionTypeText = translate('Short Answer', true);

      if (type === 'MC') {
        questionTypeText = translate('Multiple Choice', true);
      } else if (type === 'TF') {
        questionTypeText = translate('True / False', true);
      }

      quizProgressLeftChildren.push(
        <div key="question-type-container" className="question-type-container">
          <span
            className={cx(
              'question-type-dot',
              `${type.toLowerCase()}-question-type-dot`
            )}
          />
          <span
            className={cx(
              'question-type-text',
              `${type.toLowerCase()}-question-type-text`
            )}
          >
            {questionTypeText}
          </span>
        </div>
      );
    }

    const quizProgressChildren = [
      <div key="quiz-progress-container" className="quiz-progress-container">
        {quizProgressLeftChildren}
      </div>,
    ];

    if (studentNav) {
      quizProgressChildren.push(
        <button
          key="student-nav-finish-button"
          className="button button-primary button-small pill student-nav-finish-button"
          id="student-finish-quiz"
          onClick={finishQuizClicked}
        >
          {translate('Finish Quiz', true)}
        </button>
      );
    }

    progressChildren.push(
      <div key="progress-container" className="progress-container">
        <div className="quiz-progress clearfix">{quizProgressChildren}</div>
      </div>
    );
  }
  return (
    <div>
      {quizTimerSeconds != null && (
        <div key="quiz-timer-container" className="quiz-timer-container">
          <QuizTimer quizTimerSeconds={quizTimerSeconds} onExpired={onTimerExpired} />
        </div>
      )}
      {progressChildren}
      <div className="clearfix">
        <div
          id="take-quiz-master-container"
          className={cx('take-quiz-master-container', {
            'take-quiz-master-container-nav': studentNav,
          })}
        >
          <div id="question-container">
            <div className={`${type.toLowerCase()}-question`}>
              <div
                className={cx('question-header-container clearfix', {
                  'no-image': !(imageUrl || questionMedia),
                })}
              >
                {imageUrl ? (
                  <div
                    className="student-quiz-image-container"
                    key={`student-quiz-image-container-${imageUrl}`}
                  >
                    <ActivityImage imageUrl={imageUrl} />
                  </div>
                ) : null}
                {questionMedia ? (
                  <MediaPlayer
                    key={`question-video-${currentQuestion}`}
                    url={questionMedia.url}
                    start={questionMedia?.start}
                    end={questionMedia?.end}
                    type={MediaResourceTypes.Video}
                    controls
                    containerStyle="student-quiz-media-container"
                    playerStyle="activity-media-container"
                    ErrorScreen={VideoErrorScreen}
                  />
                ) : null}
                {question_text && question_text.length > 0 ? (
                  <div
                    className={cx('question-explanation-container clearfix', {
                      'no-image': !imageUrl,
                    })}
                  >
                    <div className="question-text">
                      <pre
                        dangerouslySetInnerHTML={{
                          __html: question_text,
                        }}
                      />
                    </div>
                    {showGradingWeight && (
                      <Point gradingWeight={grading_weight} />
                    )}
                  </div>
                ) : (
                  showGradingWeight && <Point gradingWeight={grading_weight} />
                )}
              </div>
              <div className="question-answer-container">
                {questionAnswerContainerChildren}
              </div>
              <div className="nav-container">{navContainerChildren}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="nav-pagination">{navPaginationChildren}</div>
    </div>
  );
};

export default StudentQuizView;
